import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { usePermissions } from './contexts/PermissionsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ServerNotificationProvider } from './contexts/ServerNotificationContext';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Departments from './pages/Departments';
import AttendanceManagement from './pages/AttendanceManagement';
import SalaryManagement from './pages/SalaryManagement';
import MyProfile from './pages/MyProfile';
import MyAttendance from './pages/MyAttendance';
import MyAbsences from './pages/MyAbsences';
import MySalary from './pages/MySalary';
import AttestationRequests from './pages/AttestationRequests';
import RemoteAttendance from './pages/RemoteAttendance';
import Settings from './pages/Settings';
import PermissionsTabs from './pages/PermissionsTabs';
import Notifications from './pages/Notifications';
import NotificationSettings from './pages/NotificationSettings';
import AllRequests from './pages/AllRequests';
import MyRequests from './pages/MyRequests';
import MyTasks from './pages/MyTasks';
import Projects from './pages/Projects';
import Rewards from './pages/Rewards';
import MyRewards from './pages/MyRewards';
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';
import './App.css';

const PermissionRoute = ({ permission, children }) => {
  const { hasPermission, loading } = usePermissions();

  if (loading) {
    return <LoadingScreen />;
  }

  return hasPermission(permission) ? children : <Navigate to="/dashboard" />;
};

function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      <Routes>
        <Route 
          path="/login" 
          element={isAuthenticated() ? <Navigate to="/dashboard" /> : <Login />} 
        />
        <Route 
          path="/forgot-password" 
          element={isAuthenticated() ? <Navigate to="/dashboard" /> : <ForgotPassword />} 
        />
        <Route 
          path="/*" 
          element={
            isAuthenticated() ? (
              <NotificationProvider>
                <PermissionsProvider>
                  <ServerNotificationProvider>
                    <Layout>
                    <Routes>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/users" element={<PermissionRoute permission="users"><Users /></PermissionRoute>} />
                      <Route path="/departments" element={<PermissionRoute permission="departments"><Departments /></PermissionRoute>} />
                      <Route path="/attendance-mgmt" element={<PermissionRoute permission="attendance_management"><AttendanceManagement /></PermissionRoute>} />
                      <Route path="/leave-requests" element={<Navigate to="/all-requests" />} />
                      <Route path="/salary-mgmt" element={<PermissionRoute permission="salary_management"><SalaryManagement /></PermissionRoute>} />
                      <Route path="/attestations" element={<Navigate to="/all-requests" />} />
                      <Route path="/all-requests" element={<PermissionRoute permission="all_requests"><AllRequests /></PermissionRoute>} />
                      <Route path="/my-profile" element={<MyProfile />} />
                      <Route path="/my-attendance" element={<PermissionRoute permission="my_attendance"><MyAttendance /></PermissionRoute>} />
                      <Route path="/my-absences" element={<PermissionRoute permission="my_attendance"><MyAbsences /></PermissionRoute>} />
                      <Route path="/my-salary" element={<PermissionRoute permission="my_salary"><MySalary /></PermissionRoute>} />
                      <Route path="/projects" element={<PermissionRoute permission="projects"><Projects /></PermissionRoute>} />
                      <Route path="/my-tasks" element={<PermissionRoute permission="my_tasks"><MyTasks /></PermissionRoute>} />
                      <Route path="/rewards" element={<PermissionRoute permission="rewards"><Rewards /></PermissionRoute>} />
                      <Route path="/my-rewards" element={<PermissionRoute permission="my_rewards"><MyRewards /></PermissionRoute>} />
                      <Route path="/my-attestations" element={<PermissionRoute permission="my_requests"><AttestationRequests /></PermissionRoute>} />
                      <Route path="/my-requests" element={<PermissionRoute permission="my_requests"><MyRequests /></PermissionRoute>} />
                      <Route path="/remote-work" element={<PermissionRoute permission="remote_work"><RemoteAttendance /></PermissionRoute>} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/notification-settings" element={<NotificationSettings />} />
                      <Route
                        path="/permissions"
                        element={
                          <PermissionRoute permission="permissions">
                            <PermissionsTabs />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <PermissionRoute permission="settings">
                            <Settings />
                          </PermissionRoute>
                        }
                      />
                      <Route path="/" element={<Navigate to="/dashboard" />} />
                    </Routes>
                  </Layout>
                  </ServerNotificationProvider>
                </PermissionsProvider>
              </NotificationProvider>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
      </Routes>
    </div>
  );
}

export default App;
